import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight, Check, Sparkles } from "lucide-react";
import { LANES, getLane, isVoiceConflicted, relationshipToAudience, getLaneAmbient } from "../lib/moodLanes";

/**
 * Optional "Shape my mood" 6-step progressive guidance modal.
 *
 * Steps:
 *  0. Lane       — broad emotional category (8 lanes)
 *  1. Sub-mood   — lane-curated chips
 *  2. Temperature— lane-curated emotional intensity
 *  3. Occasion   — lane-curated context (the *why*)
 *  4. Relation   — who is this written FOR
 *  5. Voice + optional extra free text
 *
 * Output feeds the existing personalize state — backend understands all 5 keys.
 */
export default function MoodLanes({ open, onOpenChange, onApply, suggestion = null }) {
    const [step, setStep] = useState(0);
    const [laneKey, setLaneKey] = useState(null);
    const [subMood, setSubMood] = useState(null);
    const [temperature, setTemperature] = useState(null);
    const [occasion, setOccasion] = useState(null);
    const [relationship, setRelationship] = useState(null);
    const [voice, setVoice] = useState(null);
    const [extra, setExtra] = useState("");
    const [celebrate, setCelebrate] = useState(null); // { id } — fires big check overlay on each step completion
    const [appliedSuggestion, setAppliedSuggestion] = useState(null); // tracks which keys came from AI

    useEffect(() => {
        if (!open) {
            setStep(0);
            setLaneKey(null); setSubMood(null); setTemperature(null);
            setOccasion(null); setRelationship(null); setVoice(null);
            setExtra(""); setCelebrate(null); setAppliedSuggestion(null);
        }
    }, [open]);

    // Phase 3·a — when a suggestion arrives (sync on open OR async after open),
    // pre-light chips for any field the user hasn't already touched manually.
    useEffect(() => {
        if (!open || !suggestion) return;
        let applied = { ...(appliedSuggestion || {}) };
        if (suggestion.lane && !laneKey) { setLaneKey(suggestion.lane); applied.lane = suggestion.lane; }
        if (suggestion.subMood && !subMood) { setSubMood(suggestion.subMood); applied.subMood = suggestion.subMood; }
        if (suggestion.temperature && !temperature) { setTemperature(suggestion.temperature); applied.temperature = suggestion.temperature; }
        if (suggestion.occasion && !occasion) { setOccasion(suggestion.occasion); applied.occasion = suggestion.occasion; }
        if (suggestion.relationship && !relationship) { setRelationship(suggestion.relationship); applied.relationship = suggestion.relationship; }
        setAppliedSuggestion(applied);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, suggestion]);

    // A field is "AI-suggested" if appliedSuggestion still has the SAME value the
    // user holds. The moment the user picks something different, it's no longer
    // an AI pre-light (because we never overwrite manual picks).
    const isAISuggested = (field, value) =>
        !!appliedSuggestion && appliedSuggestion[field] === value && value != null;

    // Fire a big check stamp whenever a step transitions from empty → filled.
    useEffect(() => { if (laneKey)      setCelebrate({ id: Date.now() + "-l" }); }, [laneKey]);
    useEffect(() => { if (subMood)      setCelebrate({ id: Date.now() + "-s" }); }, [subMood]);
    useEffect(() => { if (temperature)  setCelebrate({ id: Date.now() + "-t" }); }, [temperature]);
    useEffect(() => { if (occasion)     setCelebrate({ id: Date.now() + "-o" }); }, [occasion]);
    useEffect(() => { if (relationship) setCelebrate({ id: Date.now() + "-r" }); }, [relationship]);
    useEffect(() => { if (voice)        setCelebrate({ id: Date.now() + "-v" }); }, [voice]);

    const lane = laneKey ? getLane(laneKey) : null;
    const TOTAL = 6;

    const next = () => setStep((s) => Math.min(TOTAL - 1, s + 1));
    const back = () => setStep((s) => Math.max(0, s - 1));

    const apply = () => {
        // Lane-explicit relationship → audience map (Phase 3·d). Replaces the
        // brittle string-shape ladder. Falls back to voice.audience if no
        // relationship was picked.
        const targetAudience = relationshipToAudience(laneKey, relationship, voice?.audience || null);

        const personalize = {
            audience: targetAudience,
            tone: voice?.tone || null,
            locality: "",
            tags: [subMood, temperature, voice?.key, occasion, relationship].filter(Boolean).slice(0, 6),
            lane: laneKey || null,
            voice: voice?.key || null,
            temperature: temperature || null,
            occasion: occasion || null,
            relationship: relationship || null,
        };
        onApply({
            personalize,
            seed_text: extra.trim(),
            lane_label: lane?.label,
            voice_label: voice?.label,
        });
        onOpenChange(false);
    };

    const canNext =
        (step === 0 && laneKey) ||
        (step === 1 && subMood) ||
        (step === 2 && temperature) ||
        (step === 3 && occasion) ||
        (step === 4 && relationship) ||
        (step === 5);

    const STEP_TITLES = [
        "what kind of mood?",
        "how specific?",
        "how does it feel?",
        "what's the occasion?",
        "who is this for?",
        "what voice?",
    ];

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => onOpenChange(false)}
                        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.94, y: 18 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 10 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                        data-testid="mood-lanes-sheet"
                    >
                        <div className="relative w-full max-w-md max-h-[88vh] rounded-3xl border border-app/30 bg-app-2 shadow-[0_30px_80px_rgba(0,0,0,0.7)] overflow-hidden flex flex-col pointer-events-auto">
                            {/* Celebratory big check stamp — fires each time a step gets a value */}
                            <AnimatePresence>
                                {celebrate && (
                                    <motion.div
                                        key={celebrate.id}
                                        initial={{ opacity: 0, scale: 0.3, rotate: -22 }}
                                        animate={{ opacity: [0, 1, 1, 0], scale: [0.3, 1.15, 1.0, 1.4], rotate: [-22, -8, -2, 6] }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 1.1, times: [0, 0.18, 0.55, 1], ease: [0.16, 1, 0.3, 1] }}
                                        onAnimationComplete={() => setCelebrate(null)}
                                        className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center"
                                    >
                                        <div
                                            className="flex items-center justify-center h-32 w-32 rounded-full shadow-[0_0_60px_rgba(52,211,153,0.55)]"
                                            style={{
                                                background: "radial-gradient(circle at 30% 30%, #A7F3D0 0%, #34D399 55%, #10B981 100%)",
                                            }}
                                        >
                                            <Check className="w-16 h-16 text-emerald-950" strokeWidth={3.5} />
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            <div className="absolute inset-0 grain pointer-events-none" />
                            {/* Phase 3·c — Live ambient lane wash. Layered between bg + grain.
                                Cross-fades smoothly when laneKey changes. ~6-10% strength. */}
                            <AnimatePresence>
                                {laneKey && getLaneAmbient(laneKey) && (
                                    <motion.div
                                        key={laneKey}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                                        className="pointer-events-none absolute inset-0"
                                        style={{
                                            background:
                                                `radial-gradient(120% 80% at 50% 0%, ${getLaneAmbient(laneKey).from}55 0%, ${getLaneAmbient(laneKey).to}00 65%)`,
                                            mixBlendMode: "screen",
                                        }}
                                        data-testid={`lane-ambient-${laneKey}`}
                                    />
                                )}
                            </AnimatePresence>
                            <div className="relative px-6 pt-6 pb-3 shrink-0">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3">
                                            shape my mood · {step + 1} / {TOTAL}
                                        </span>
                                        <h3 className="mt-1 font-serif font-light italic text-app text-2xl tracking-tight">
                                            {STEP_TITLES[step]}
                                        </h3>
                                    </div>
                                    <button onClick={() => onOpenChange(false)} className="p-2 rounded-full text-app-2 hover:text-app hover:bg-surface-2 transition-colors" data-testid="mood-lanes-close">
                                        <X className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </div>
                                {/* Phase 3·a — AI pre-light banner. Only renders if at least one chip was pre-lit by AI. */}
                                {appliedSuggestion && Object.keys(appliedSuggestion).length > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                        className="mt-3 flex items-center gap-2 px-3 py-2 rounded-full border border-amber-300/30 bg-amber-300/8"
                                        data-testid="mood-lanes-ai-banner"
                                    >
                                        <Sparkles className="w-3 h-3 text-amber-300/90" strokeWidth={2} />
                                        <span className="font-serif italic text-[12px] text-amber-100/85 leading-tight">
                                            picked some for you — tap to change anything
                                        </span>
                                    </motion.div>
                                )}
                                {/* Progress chips — 6-up */}
                                <div className="mt-4 flex items-center gap-1.5 flex-wrap">
                                    {[
                                        { i: 0, done: !!laneKey,      label: lane?.short_label || lane?.label?.split(" ")[0]?.toLowerCase() || "mood" },
                                        { i: 1, done: !!subMood,      label: subMood || "sub" },
                                        { i: 2, done: !!temperature,  label: temperature || "temp" },
                                        { i: 3, done: !!occasion,     label: occasion || "occ" },
                                        { i: 4, done: !!relationship, label: relationship || "for" },
                                        { i: 5, done: !!voice,        label: voice?.label || "voice" },
                                    ].map(({ i, done, label }) => (
                                        <motion.div
                                            key={i}
                                            initial={false}
                                            animate={{ scale: done ? 1.02 : 0.96 }}
                                            transition={{ type: "spring", stiffness: 380, damping: 24 }}
                                            className={`flex items-center gap-1 px-2 py-1 rounded-full border transition-colors ${
                                                done
                                                    ? "border-emerald-400/70 bg-emerald-400/12"
                                                    : i === step
                                                    ? "border-app/40 bg-surface-2"
                                                    : "border-app/15 bg-transparent"
                                            }`}
                                            data-testid={`mood-lanes-progress-${i}`}
                                        >
                                            <AnimatePresence mode="wait" initial={false}>
                                                {done ? (
                                                    <motion.span
                                                        key="done"
                                                        initial={{ scale: 0, rotate: -90 }}
                                                        animate={{ scale: 1, rotate: 0 }}
                                                        exit={{ scale: 0 }}
                                                        transition={{ type: "spring", stiffness: 460, damping: 16 }}
                                                        className="flex items-center justify-center h-4 w-4 rounded-full bg-emerald-400 text-emerald-950 shadow-[0_0_8px_rgba(52,211,153,0.5)]"
                                                    >
                                                        <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                                                    </motion.span>
                                                ) : (
                                                    <motion.span
                                                        key="dot"
                                                        initial={{ scale: 0 }}
                                                        animate={{ scale: 1 }}
                                                        exit={{ scale: 0 }}
                                                        className="block h-1.5 w-1.5 rounded-full bg-app-3"
                                                    />
                                                )}
                                            </AnimatePresence>
                                            <span className={`font-sans text-[9px] uppercase tracking-[0.16em] truncate max-w-[52px] ${done ? "text-emerald-200" : "text-app-3"}`}>
                                                {label}
                                            </span>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>

                            <div className="relative flex-1 overflow-y-auto no-scrollbar px-6 pb-4">
                                {/* Step 0: Lanes */}
                                {step === 0 && (
                                    <div className="grid grid-cols-2 gap-2.5 mt-2" data-testid="mood-lanes-step-0">
                                        {LANES.map((l) => {
                                            const active = laneKey === l.key;
                                            const aiPicked = active && isAISuggested("lane", l.key);
                                            return (
                                                <button
                                                    key={l.key}
                                                    onClick={() => setLaneKey(l.key)}
                                                    data-testid={`lane-${l.key}`}
                                                    className={`p-4 rounded-xl border text-left transition-all ${
                                                        aiPicked
                                                            ? "border-amber-300/60 bg-amber-300/8 shadow-[0_0_22px_rgba(252,211,77,0.18)]"
                                                            : active
                                                            ? "border-app bg-surface-3"
                                                            : "border-app/20 bg-surface-1 hover:bg-surface-2 hover:border-app/40"
                                                    }`}
                                                >
                                                    <div className="font-serif italic text-app text-xl mb-1.5" style={{ opacity: 0.85 }}>
                                                        {l.glyph}
                                                    </div>
                                                    <div className="font-sans text-[12px] text-app leading-snug">
                                                        {l.label}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Step 1: Sub-moods */}
                                {step === 1 && lane && (
                                    <div className="mt-2" data-testid="mood-lanes-step-1">
                                        <p className="font-serif italic text-app-2 text-sm mb-4">
                                            <span className="text-app">{lane.label}</span> — pick one that fits closest.
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {lane.subMoods.map((s) => {
                                                const active = subMood === s;
                                                const aiPicked = active && isAISuggested("subMood", s);
                                                return (
                                                    <button
                                                        key={s}
                                                        onClick={() => setSubMood(s)}
                                                        data-testid={`submood-${s}`}
                                                        className={`px-3.5 py-2 rounded-full border text-sm font-sans transition-colors ${
                                                            aiPicked
                                                                ? "border-amber-300/70 bg-amber-300/15 text-amber-100"
                                                                : active
                                                                ? "border-app bg-cta text-app-inverse"
                                                                : "border-app/30 text-app-2 hover:text-app hover:border-app/60"
                                                        }`}
                                                    >
                                                        {s.replace(/-/g, " ")}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Step 2: Temperature — emotional intensity, lane-curated */}
                                {step === 2 && lane && (
                                    <div className="mt-2" data-testid="mood-lanes-step-2">
                                        <p className="font-serif italic text-app-2 text-sm mb-4">
                                            <span className="text-app">{(subMood || "").replace(/-/g, " ")}</span>
                                            {" "}— pick the temperature.
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {(lane.temperatures || []).map((t) => {
                                                const active = temperature === t;
                                                const aiPicked = active && isAISuggested("temperature", t);
                                                return (
                                                    <button
                                                        key={t}
                                                        onClick={() => setTemperature(t)}
                                                        data-testid={`temperature-${t}`}
                                                        className={`px-3.5 py-2 rounded-full border text-sm font-sans transition-colors ${
                                                            aiPicked
                                                                ? "border-amber-300/70 bg-amber-300/15 text-amber-100"
                                                                : active
                                                                ? "border-app bg-cta text-app-inverse"
                                                                : "border-app/30 text-app-2 hover:text-app hover:border-app/60"
                                                        }`}
                                                    >
                                                        {t.replace(/-/g, " ")}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Step 3: Occasion — context / why */}
                                {step === 3 && lane && (
                                    <div className="mt-2" data-testid="mood-lanes-step-3">
                                        <p className="font-serif italic text-app-2 text-sm mb-4">
                                            <span className="text-app">{(temperature || "").replace(/-/g, " ")}</span>
                                            {" "}— what's the occasion?
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {(lane.occasions || []).map((o) => {
                                                const active = occasion === o;
                                                const aiPicked = active && isAISuggested("occasion", o);
                                                return (
                                                    <button
                                                        key={o}
                                                        onClick={() => setOccasion(o)}
                                                        data-testid={`occasion-${o.replace(/\s+/g, "-").toLowerCase()}`}
                                                        className={`px-3.5 py-2 rounded-full border text-sm font-sans transition-colors ${
                                                            aiPicked
                                                                ? "border-amber-300/70 bg-amber-300/15 text-amber-100"
                                                                : active
                                                                ? "border-app bg-cta text-app-inverse"
                                                                : "border-app/30 text-app-2 hover:text-app hover:border-app/60"
                                                        }`}
                                                    >
                                                        {o}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <p className="mt-3 font-sans text-[10px] uppercase tracking-[0.25em] text-app-3/70">
                                            optional — pick "just because" if no occasion
                                        </p>
                                    </div>
                                )}

                                {/* Step 4: Relationship targeting — who is this for */}
                                {step === 4 && lane && (
                                    <div className="mt-2" data-testid="mood-lanes-step-4">
                                        <p className="font-serif italic text-app-2 text-sm mb-4">
                                            written for <span className="text-app">whom?</span>
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {(lane.relationships || []).map((r) => {
                                                const active = relationship === r;
                                                const aiPicked = active && isAISuggested("relationship", r);
                                                return (
                                                    <button
                                                        key={r}
                                                        onClick={() => setRelationship(r)}
                                                        data-testid={`relationship-${r.replace(/\s+/g, "-").toLowerCase()}`}
                                                        className={`px-3.5 py-2 rounded-full border text-sm font-sans transition-colors ${
                                                            aiPicked
                                                                ? "border-amber-300/70 bg-amber-300/15 text-amber-100"
                                                                : active
                                                                ? "border-app bg-cta text-app-inverse"
                                                                : "border-app/30 text-app-2 hover:text-app hover:border-app/60"
                                                        }`}
                                                    >
                                                        {r}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <p className="mt-3 font-sans text-[10px] uppercase tracking-[0.25em] text-app-3/70">
                                            shapes the address — i / you / we / they
                                        </p>
                                    </div>
                                )}

                                {/* Step 5: Voice + optional extra (with conflict hints) */}
                                {step === 5 && lane && (
                                    <div className="mt-2 space-y-5" data-testid="mood-lanes-step-5">
                                        <div>
                                            <p className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3 mb-2">
                                                voice
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {lane.voices.map((v) => {
                                                    const active = voice?.key === v.key;
                                                    const conflict = !active && isVoiceConflicted(laneKey, temperature, v.key);
                                                    return (
                                                        <button
                                                            key={v.key}
                                                            onClick={() => setVoice(v)}
                                                            data-testid={`voice-${v.key}`}
                                                            title={conflict ? `feels a bit at odds with "${temperature}"` : undefined}
                                                            className={`px-3.5 py-2 rounded-full border text-sm font-sans transition-all ${
                                                                active
                                                                    ? "border-app bg-cta text-app-inverse"
                                                                    : conflict
                                                                    ? "border-app/15 text-app-3 opacity-50 hover:opacity-80 hover:border-app/30"
                                                                    : "border-app/30 text-app-2 hover:text-app hover:border-app/60"
                                                            }`}
                                                        >
                                                            {v.label}
                                                            {conflict && (
                                                                <span className="ml-1.5 text-[9px] uppercase tracking-[0.1em] opacity-70">·off</span>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {voice && isVoiceConflicted(laneKey, temperature, voice.key) && (
                                                <p className="mt-3 font-serif italic text-[12px] text-amber-300/80">
                                                    that voice tugs against "{temperature}" — sometimes dissonance is the point. proceed if it's intentional.
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <p className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3 mb-2">
                                                say what you couldn't fully say · optional
                                            </p>
                                            <textarea
                                                value={extra}
                                                onChange={(e) => setExtra(e.target.value)}
                                                maxLength={240}
                                                rows={3}
                                                placeholder={lane.prompt}
                                                data-testid="mood-lanes-extra"
                                                className="w-full bg-surface-2 border border-app/30 rounded-xl px-4 py-3 text-app font-serif italic text-lg leading-snug outline-none focus:border-app/60 placeholder:text-app-3 resize-none"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Footer nav */}
                            <div className="relative px-6 pb-5 pt-3 shrink-0 flex items-center justify-between gap-3 border-t border-app/20 bg-app-2">
                                <button
                                    onClick={step === 0 ? () => onOpenChange(false) : back}
                                    data-testid="mood-lanes-back"
                                    className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-app/30 text-app-2 hover:text-app hover:bg-surface-2 transition-colors"
                                >
                                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                                    <span className="font-sans text-xs uppercase tracking-[0.25em]">
                                        {step === 0 ? "cancel" : "back"}
                                    </span>
                                </button>

                                {step < TOTAL - 1 ? (
                                    <motion.button
                                        whileTap={{ scale: 0.96 }}
                                        onClick={next}
                                        disabled={!canNext}
                                        data-testid="mood-lanes-next"
                                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full bg-cta text-app-inverse font-sans text-sm font-medium tracking-wide hover:opacity-90 transition-opacity disabled:opacity-30"
                                    >
                                        next
                                        <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                                    </motion.button>
                                ) : (
                                    <motion.button
                                        onClick={apply}
                                        data-testid="mood-lanes-apply"
                                        initial={{ scale: 0.9, opacity: 0.6 }}
                                        animate={{
                                            scale: [0.96, 1.0, 0.99, 1.0],
                                            opacity: 1,
                                            boxShadow: [
                                                "0 0 0 rgba(52, 211, 153, 0)",
                                                "0 0 24px rgba(52, 211, 153, 0.55)",
                                                "0 0 12px rgba(52, 211, 153, 0.35)",
                                                "0 0 24px rgba(52, 211, 153, 0.55)",
                                            ],
                                        }}
                                        transition={{
                                            scale: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
                                            boxShadow: { duration: 2.4, repeat: Infinity, ease: "easeInOut" },
                                        }}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.97 }}
                                        className="relative flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-full font-sans text-sm font-semibold tracking-wide text-emerald-950 overflow-hidden"
                                        style={{
                                            background: "linear-gradient(135deg, #6EE7B7 0%, #34D399 45%, #10B981 100%)",
                                        }}
                                    >
                                        {/* Sheen sweep */}
                                        <motion.span
                                            className="pointer-events-none absolute inset-0"
                                            initial={{ x: "-110%" }}
                                            animate={{ x: "110%" }}
                                            transition={{ duration: 1.8, repeat: Infinity, repeatDelay: 0.6, ease: "easeInOut" }}
                                            style={{
                                                background: "linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.55) 50%, transparent 65%)",
                                                mixBlendMode: "screen",
                                            }}
                                        />
                                        <Sparkles className="relative w-4 h-4" strokeWidth={2} />
                                        <span className="relative">make my moodcard</span>
                                        <Check className="relative w-4 h-4" strokeWidth={2.4} />
                                    </motion.button>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
